import { useState } from "react";
import { Box, Button, Card, CardContent, Typography, TextField, MenuItem, Checkbox, Container } from "@mui/material";
import { axiosClient } from "../api/proxy/AxiosClient";

const CreateUser = ({
   typeIsActive = false,
   onCancel,
   onSubmit,
   onRestore,
   onAdd
}) => {
  
  const [step, setStep] = useState(1); // 1 = email check, 2 = form
  const [loading, setLoading] = useState(false);
  const [readonly, setReadonly] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    firstName: null,
    lastName: null,
    notificationType: "email",
    email: '',
    password: null,
    confirmPassword: null,
    phoneNumber: null,
    telegramId: null,
    portrait: null,
    comment: null,
    type: "student",
    isDemo: false,
    isActive: false
  });

  const validateEmail = () => {
    let errs = {};

    // email
    if (!form.email) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Invalid email format";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const validate = () => {
    let errs = {};

    // email
    if (!form.email) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Invalid email format";
    }

    // password
    if (!form.password) {
      errs.password = "Password is required";
    } else if (form.password.length < 3) {
      errs.password = "Password must be at least 3 characters";
    } else if (form.password.length > 64) {
      errs.password = "Password must be max 64 characters";
    }

    // confirm password
    if (form.password) {
      if (!form.confirmPassword) {
        errs.confirmPassword = "Please confirm password";
      } else if (form.password !== form.confirmPassword) {
        errs.confirmPassword = "Passwords do not match";
      }
    }

    // phone number (optional)
    if (form.phoneNumber && !/^\+?[0-9]{7,15}$/.test(form.phoneNumber)) {
      errs.phoneNumber = "Invalid phone number";
    }

    // portrait (optional url)
    if (form.portrait && !/^https?:\/\/.+/.test(form.portrait)) {
      errs.portrait = "Invalid URL";
    }

    // telegramId min length 3
    if (form.telegramId && form.telegramId.length < 3) {
      errs.telegramId = "Telegram ID must be at least 3 characters";
    }

    // firstName / lastName length
    if (form.firstName && (form.firstName.length < 2 || form.firstName.length > 32)) {
      errs.firstName = "First name must be 2-32 characters";
    }
    if (form.lastName && (form.lastName.length < 2 || form.lastName.length > 32)) {
      errs.lastName = "Last name must be 2-32 characters";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors({ ...errors, [name]: null });
  };

  const handleCheckEmail = async () => {
    if (!validateEmail()) return;
    setLoading(true);
    try {
      const res = await axiosClient.get(`/users/email/${form.email}`);
      if (!!res.data.id) {
        setForm((prev) => ({ ...prev, ...res.data }));
        setReadonly(true);
        setDeleted(!!res.data.isDeleted);
        onAdd(res.data);
      } else {
        setReadonly(false);
      }
      setStep(2);
    } catch (err) {
        console.error("Error checking email", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    try {
      const payload = { ...form };
      delete payload.confirmPassword;
      onSubmit(payload);
    } catch (err) {
      console.error("Error creating user", err);
    }
  };

  const handleRestore = async () => {
    try {
      const payload = { ...form };
      onRestore(payload.uid);
    } catch (err) {
      console.error("Error restoring user", err);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 4,
        width: 300,
        overflow: "hidden",
        bgcolor: "#ffffff",
        border: "1px solid #e0e0e0",
      }}
    >
      <CardContent>
        {step === 1 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Check user by email</Typography>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email || ""}
              onChange={handleChange}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
            />
            <Button
              variant="contained"
              onClick={handleCheckEmail}
              disabled={loading || !form.email}
            >
              Check
            </Button>
          </Box>
        )}

        {step === 2 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">Create / View User</Typography>

            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName || ""}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName || ""}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
            />
            <TextField
              label="Notification Type"
              name="notificationType"
              value={form.notificationType || ""}
              disabled
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email || ""}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              disabled
            />
            {!readonly && (
              <>
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password || ""}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password}
                />
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword || ""}
                  onChange={handleChange}
                  disabled={!form.password}
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />
              </>
            )}
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={form.phoneNumber || ""}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
            {/*
            <TextField
              label="Telegram ID"
              name="telegramId"
              value={form.telegramId}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              error={!!errors.telegramId}
              helperText={errors.telegramId}
            />
            <TextField
              label="Portrait URL"
              name="portrait"
              value={form.portrait}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              error={!!errors.portrait}
              helperText={errors.portrait}
            />
            */}
            <TextField
              label="Comment"
              name="comment"
              value={form.comment || ""}
              onChange={handleChange}
              slotProps={{ input: { readOnly: readonly } }}
              fullWidth
              multiline
              rows={3}
            />
            {typeIsActive && (
              <TextField
                select
                label="User Type"
                name="type"
                value={form.type || ""}
                onChange={handleChange}
                slotProps={{ input: { readOnly: readonly } }}
                fullWidth
              >
                <MenuItem value="superuser">superuser</MenuItem>
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="teacher">teacher</MenuItem>
                <MenuItem value="student">student</MenuItem>
                <MenuItem value="outwrite">outwrite</MenuItem>
                <MenuItem value="outwrite_learner">outwrite_learner</MenuItem>
              </TextField>
            )}
            <Container>
              <Checkbox title="is demo !" disabled={readonly} checked={form.isDemo || false} onChange={(e) => setForm((prev) => ({ ...prev, isDemo: e.target.checked }))} /> Is Demo User
            </Container>
            <Container>
              <Checkbox title="no activation needed" disabled={readonly} checked={form.isActive || false} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} /> Is Active User
            </Container>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              {!readonly && (
                <Button variant="contained" onClick={handleSubmit}>
                  Submit
                </Button>
              )}
              {deleted && (
                <Button variant="contained" onClick={handleRestore}>
                  Restore
                </Button>
              )}
            </Box>
           </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CreateUser;
